import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DragEndEvent } from "@dnd-kit/core";
import { useReorderSensors } from "@/hooks/useReorderSensors";
import { reorderByDragEnd } from "@/lib/reorder";
import { houseService } from "@/services/houseService";
import { deviceKeys } from "@/services/deviceService";
import { toastApiError } from "@/lib/errorMessages";
import type { Device, HouseDevice } from "@/types";

/** Persist house device order (optimistic cache + API). */
export function useHouseDevicesReorder(houseId: string) {
  const queryClient = useQueryClient();
  const queryKey = deviceKeys.byHouse(houseId);

  const sensors = useReorderSensors();

  const reorderMutation = useMutation({
    mutationFn: (deviceIds: string[]) =>
      houseService.reorderDevices(houseId, deviceIds),
    onSuccess: (devices) => {
      queryClient.setQueryData<HouseDevice[]>(queryKey, devices);
    },
    onError: (err) => {
      toastApiError(err, "reorderDevices");
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const commitOrder = (reordered: Device[]) => {
    queryClient.setQueryData<HouseDevice[]>(queryKey, reordered as HouseDevice[]);
    reorderMutation.mutate(reordered.map((device) => device.id));
  };

  const handleDragEnd = (event: DragEndEvent, devices: HouseDevice[]) => {
    const reordered = reorderByDragEnd(devices, event, (device) => device.id);
    if (!reordered) return;
    commitOrder(reordered);
  };

  return {
    sensors,
    handleDragEnd,
    commitOrder,
    isReordering: reorderMutation.isPending,
  };
}
